import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { RouterProvider } from "@tanstack/react-router";
import { useEffect, useMemo } from "react";

import { manageAttachmentBlobUrlLifecycle } from "./api/hooks/useAttachmentBlobUrl";
import { useApiClient } from "./api/useApiClient";
import { ThemeProvider } from "./components/theme/ThemeProvider";
import { ToastProvider } from "./components/ui/toast";
import { router } from "./router";

export default function App() {
  const apiClient = useApiClient();
  const queryClient = useMemo(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            retry: false,
            refetchOnWindowFocus: false,
            // Serve recently-fetched data from cache so revisiting a screen via
            // the sidebar is instant instead of refetching on every mount.
            // Mutations invalidate explicitly (every mutation hook calls
            // invalidateQueries), so staleTime never masks a write.
            staleTime: 60_000,
            // NOTE: keepPreviousData is intentionally NOT a global default.
            // Globally, it renders the prior query key's rows (e.g. the
            // unfiltered list) while a newly-filtered query loads, which reads
            // as a flash of unfiltered data. It is opted into per query only
            // where pagination smoothness is wanted and scoped to the same
            // filter set (see useTrades).
          },
        },
      }),
    [],
  );

  // Revoke attachment blob URLs when their cache entry is evicted (not on any
  // single component's unmount, which would break a still-mounted sibling that
  // shares the same query key). Registered once against the stable client.
  useEffect(
    () => manageAttachmentBlobUrlLifecycle(queryClient),
    [queryClient],
  );

  return (
    <ThemeProvider>
      <QueryClientProvider client={queryClient}>
        <ToastProvider>
          <RouterProvider
            router={router}
            context={{ queryClient, apiClient }}
          />
        </ToastProvider>
      </QueryClientProvider>
    </ThemeProvider>
  );
}
